import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Code, Lightning, Eye, Mouse, Clock, ChatCircle, WhatsappLogo, Gear, Globe } from "@phosphor-icons/react";

const IntegrationTab = ({ apiUrl }) => {
  const [copiedWidget, setCopiedWidget] = useState(false);
  const [copiedTracking, setCopiedTracking] = useState(false);
  const [copiedWP, setCopiedWP] = useState(false);
  const [copiedCustom, setCopiedCustom] = useState(false);

  const widgetCdnUrl = `${apiUrl}/api/widget.js`;

  const widgetSnippet = `<script src="${widgetCdnUrl}" defer></script>`;

  const wpFunctionsSnippet = `// Add to your theme's functions.php
function portl_agent_scripts() {
  wp_enqueue_script(
    'portl-agent',
    '${widgetCdnUrl}',
    array(), null, true
  );
}
add_action('wp_enqueue_scripts', 'portl_agent_scripts');`;

  const trackingSnippet = `<!-- Portl Sales Agent — Single script, zero-config -->
<!-- widget.js includes the chat widget + full auto-tracking -->
<script src="${widgetCdnUrl}" defer></script>

<!-- What gets auto-tracked (no code needed): -->
<!--                                                            -->
<!-- PAGE CONTEXT                                               -->
<!--   product_page_ultragym  — URL contains /product/ultragym  -->
<!--   product_page_studio    — URL contains /product/studio    -->
<!--   utm_paid               — ?utm_source or ?utm_campaign    -->
<!--   return_visit           — localStorage flag on revisit    -->
<!--                                                            -->
<!-- DWELL TIME                                                 -->
<!--   dwell_8s               — 8 seconds on page               -->
<!--   dwell_10s              — 10 seconds on page              -->
<!--   pricing_dwell_5s       — 5 seconds viewing #pricing      -->
<!--                                                            -->
<!-- SCROLL / VIEWPORT                                          -->
<!--   pricing_scroll         — #pricing enters viewport (30%)  -->
<!--                            uses IntersectionObserver        -->
<!--                                                            -->
<!-- HOVER & CLICK (Buy Now buttons)                            -->
<!--   buy_now_hover          — mouseenter on any cart link      -->
<!--   buy_now_click          — click on any cart link           -->
<!--   buy_modal_open         — click a[data-modal="#buy_modal"]-->
<!--                                                            -->
<!-- ENGAGEMENT                                                 -->
<!--   request_callback       — click a[data-modal="#request_modal"] -->
<!--   video_watch            — <video> play event              -->
<!--   whatsapp_ctc_click     — click on #ht-ctc-chat           -->
<!--   dead_clicks            — click on non-interactive areas  -->`;

  const customEventsSnippet = `// Custom event tracking (for anything not auto-detected)
// Call portlTrack() with any event name — it fires once per session

if (window.portlTrack) {
  // Example: Track when user opens a specific product tab
  portlTrack('ultragym_specs_tab');

  // Example: Track EMI calculator interaction
  portlTrack('emi_calculator_used');

  // Example: Track comparison table view
  portlTrack('comparison_table_viewed');
}

// DOM selectors the widget auto-detects on portl.co:
//
// Buy Now buttons:
//   a[href*="portl.co/cart"]
//   a[href*="cart/?add-to-cart"]
//   .primary_btn.buylink
//   .portl_header_stikcy--btn a
//
// Buy modal:   a[data-modal="#buy_modal"]
// Callback:    a[data-modal="#request_modal"]
// Pricing:     #pricing  or  .pricing-section
// Videos:      <video> elements
// WhatsApp:    #ht-ctc-chat`;

  const copyToClipboard = async (text, setter, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success(`${label} copied!`);
      setTimeout(() => setter(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const CopyBtn = ({ copied, onClick, label }) => (
    <button
      onClick={onClick}
      data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
        copied
          ? "bg-[#E85D24]/20 border border-[#E85D24] text-[#E85D24]"
          : "bg-[#E85D24] text-white hover:opacity-85"
      }`}
    >
      {copied ? <><Check size={14} weight="bold" /> Copied!</> : <><Copy size={14} weight="bold" /> {label}</>}
    </button>
  );

  const events = [
    { name: "product_page_ultragym", points: 2, desc: "Viewed UltraGym page", icon: Eye, method: "URL match" },
    { name: "product_page_studio", points: 2, desc: "Viewed Studio page", icon: Eye, method: "URL match" },
    { name: "dwell_8s", points: 2, desc: "8s on page", icon: Clock, method: "setTimeout" },
    { name: "dwell_10s", points: 2, desc: "10s on page", icon: Clock, method: "setTimeout" },
    { name: "utm_paid", points: 5, desc: "From paid ad", icon: Lightning, method: "URLSearchParams" },
    { name: "return_visit", points: 6, desc: "Return visitor", icon: Eye, method: "localStorage" },
    { name: "video_watch", points: 5, desc: "Played video", icon: Eye, method: "play event" },
    { name: "pricing_scroll", points: 8, desc: "#pricing in viewport", icon: Mouse, method: "IntersectionObserver" },
    { name: "pricing_dwell_5s", points: 5, desc: "5s on pricing section", icon: Clock, method: "IntersectionObserver + timer" },
    { name: "buy_modal_open", points: 6, desc: "Opened Buy modal", icon: Mouse, method: "click listener" },
    { name: "buy_now_hover", points: 8, desc: "Hovered Buy Now", icon: Mouse, method: "mouseenter listener" },
    { name: "buy_now_click", points: 12, desc: "Clicked Buy Now", icon: Mouse, method: "click listener" },
    { name: "request_callback", points: 10, desc: "Clicked Request Callback", icon: Mouse, method: "click listener" },
    { name: "whatsapp_ctc_click", points: 8, desc: "Clicked WhatsApp CTC", icon: Mouse, method: "click listener" },
    { name: "dead_clicks", points: 3, desc: "Non-interactive click", icon: Mouse, method: "click + tag filter" }
  ];

  const tierInfo = [
    { label: "Low Intent", range: "1-3 pts", color: "#64D2FF", events: "page view, dwell, dead clicks" },
    { label: "Medium Intent", range: "4-8 pts", color: "#FFD60A", events: "UTM, return visit, video, pricing scroll, buy modal" },
    { label: "High Intent", range: "8-15 pts", color: "#FF3B30", events: "buy hover/click, callback, WhatsApp CTC" },
  ];

  return (
    <div className="space-y-6" data-testid="integration-tab">

      {/* ===== SECTION 1: WordPress Installation Guide ===== */}
      <div className="bg-[#121212] border border-[#E85D24]/40 rounded-sm p-6" data-testid="wordpress-guide">
        <div className="flex items-center gap-3 mb-5">
          <Globe size={24} weight="bold" className="text-[#E85D24]" />
          <div>
            <h2 className="text-lg font-semibold text-white">WordPress Installation</h2>
            <p className="text-sm text-[#A0A0A0]">Deploy Arya + auto-tracking on portl.co</p>
          </div>
        </div>

        {/* Step 1 */}
        <div className="mb-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-7 h-7 rounded-full bg-[#E85D24]/20 text-[#E85D24] text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            <div>
              <h3 className="text-sm font-semibold text-white">Add to functions.php</h3>
              <p className="text-xs text-[#707070]">Appearance &gt; Theme File Editor &gt; functions.php</p>
            </div>
            <div className="ml-auto">
              <CopyBtn copied={copiedWP} onClick={() => copyToClipboard(wpFunctionsSnippet, setCopiedWP, "WordPress snippet")} label="Copy" />
            </div>
          </div>
          <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-3 overflow-x-auto text-xs font-mono text-[#A0A0A0]">
            <code>{wpFunctionsSnippet}</code>
          </pre>
        </div>

        {/* Step 2: Auto-tracking explanation */}
        <div className="mb-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-7 h-7 rounded-full bg-[#E85D24]/20 text-[#E85D24] text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <div>
              <h3 className="text-sm font-semibold text-white">Auto-tracking activates on load</h3>
              <p className="text-xs text-[#707070]">No manual code needed — widget.js auto-detects portl.co DOM elements</p>
            </div>
          </div>
          <div className="bg-[#050505] border border-[#2A2A2A] rounded p-4 text-xs text-[#A0A0A0] space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { sel: 'a[href*="portl.co/cart"]', action: "Buy Now hover + click" },
                { sel: 'a[data-modal="#buy_modal"]', action: "Buy modal open (UltraGym)" },
                { sel: 'a[data-modal="#request_modal"]', action: "Request Callback click" },
                { sel: "#pricing / .pricing-section", action: "Pricing scroll + 5s dwell" },
                { sel: "<video> elements", action: "Video play detection" },
                { sel: "#ht-ctc-chat", action: "WhatsApp CTC click" },
              ].map(s => (
                <div key={s.sel} className="flex items-start gap-2 p-2 bg-[#0A0A0A] rounded border border-[#1A1A1A]">
                  <code className="text-[#FFD60A] font-mono text-[10px] flex-shrink-0 mt-0.5">{s.sel}</code>
                  <span className="text-[#666] text-[10px]">{s.action}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-7 h-7 rounded-full bg-[#E85D24]/20 text-[#E85D24] text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <div>
              <h3 className="text-sm font-semibold text-white">Set environment variables</h3>
              <p className="text-xs text-[#707070]">Required on the backend server</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { key: "ANTHROPIC_API_KEY", desc: "Claude AI model key", required: true },
              { key: "WATI_API_URL", desc: "WATI API base URL", required: true },
              { key: "WATI_API_TOKEN", desc: "WATI authorization token", required: true },
              { key: "WATI_NUMBER", desc: "WhatsApp business number", required: true },
              { key: "GOOGLE_CALENDAR_BOOKING_URL", desc: "Demo booking calendar link", required: false },
              { key: "SALES_ALERT_PHONE", desc: "Sales team WhatsApp number", required: true },
              { key: "SALES_ALERT_EMAIL", desc: "Sales team email for alerts", required: true },
              { key: "SMTP_HOST / SMTP_USER / SMTP_PASS", desc: "Email delivery config", required: false }
            ].map(v => (
              <div key={v.key} className="flex items-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-[#2A2A2A] rounded text-xs">
                <code className="text-[#E85D24] font-mono flex-shrink-0">{v.key}</code>
                <span className="text-[#555]">{v.desc}</span>
                {v.required && <span className="ml-auto text-[10px] text-[#FF3B30] font-semibold">REQ</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== SECTION 2: Widget CDN URL ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="widget-cdn-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ChatCircle size={24} weight="bold" className="text-[#E85D24]" />
            <div>
              <h2 className="text-lg font-semibold text-white">Widget CDN (widget.js)</h2>
              <p className="text-sm text-[#A0A0A0]">Chat widget + auto-tracking in one script</p>
            </div>
          </div>
          <CopyBtn copied={copiedWidget} onClick={() => copyToClipboard(widgetSnippet, setCopiedWidget, "Widget snippet")} label="Copy" />
        </div>
        <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-4 overflow-x-auto text-sm font-mono text-[#A0A0A0]" data-testid="widget-cdn-code">
          <code>{widgetSnippet}</code>
        </pre>
        <p className="mt-3 text-xs text-[#555]">
          CDN URL: <code className="text-[#E85D24]">{widgetCdnUrl}</code>
        </p>
      </div>

      {/* ===== SECTION 3: Full Tracking Reference ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Code size={24} weight="bold" className="text-[#007AFF]" />
            <div>
              <h2 className="text-lg font-semibold text-white">Auto-Tracking Reference</h2>
              <p className="text-sm text-[#A0A0A0]">Everything tracked by widget.js — no extra code needed</p>
            </div>
          </div>
          <CopyBtn copied={copiedTracking} onClick={() => copyToClipboard(trackingSnippet, setCopiedTracking, "Tracking reference")} label="Copy" />
        </div>
        <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-4 overflow-x-auto text-xs font-mono text-[#A0A0A0] leading-relaxed" data-testid="tracking-snippet-code">
          <code>{trackingSnippet}</code>
        </pre>
      </div>

      {/* ===== SECTION 4: Scoring Tiers ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Scoring Tiers (0-100 Scale)</h2>

        {/* Intent tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          {tierInfo.map(t => (
            <div key={t.label} className="rounded p-3 border" style={{ background: t.color + "08", borderColor: t.color + "40" }}>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: t.color }}>{t.label}</span>
              <p className="font-mono text-lg font-bold mt-1" style={{ color: t.color }}>{t.range}</p>
              <p className="text-[10px] text-[#888] mt-1">{t.events}</p>
            </div>
          ))}
        </div>

        {/* Chat trigger thresholds */}
        <h3 className="text-sm font-semibold text-white mb-3">Chat Trigger Thresholds</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30] rounded p-4">
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">proactive</span>
            <p className="font-mono text-2xl font-bold text-[#FF3B30] mt-1">65+</p>
            <p className="text-xs text-[#ccc] mt-2">Auto-opens chat with Arya's greeting</p>
          </div>
          <div className="bg-[#FFD60A]/10 border border-[#FFD60A] rounded p-4">
            <span className="text-[#FFD60A] text-xs font-bold uppercase tracking-wider">soft_bubble</span>
            <p className="font-mono text-2xl font-bold text-[#FFD60A] mt-1">30-64</p>
            <p className="text-xs text-[#ccc] mt-2">Shows "Any questions? I'm here." bubble</p>
          </div>
          <div className="bg-[#64D2FF]/10 border border-[#64D2FF] rounded p-4">
            <span className="text-[#64D2FF] text-xs font-bold uppercase tracking-wider">passive</span>
            <p className="font-mono text-2xl font-bold text-[#64D2FF] mt-1">&lt;30</p>
            <p className="text-xs text-[#ccc] mt-2">Chat button visible only</p>
          </div>
        </div>
      </div>

      {/* ===== SECTION 5: Event Types & Points ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6">
        <h2 className="text-lg font-semibold text-white mb-1">Behavioral Events (auto-tracked)</h2>
        <p className="text-xs text-[#555] mb-4">Each event fires once per session via <code className="text-[#E85D24]">portlTrack()</code>. Score capped at 100.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-[#2A2A2A]">
                <th className="py-2 pr-4 text-[#707070] font-medium">Event</th>
                <th className="py-2 pr-4 text-[#707070] font-medium">Points</th>
                <th className="py-2 pr-4 text-[#707070] font-medium">Description</th>
                <th className="py-2 text-[#707070] font-medium">Detection Method</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.name} className="border-b border-[#1A1A1A] hover:bg-[#0A0A0A]" data-testid={`event-${event.name}`}>
                  <td className="py-2 pr-4">
                    <code className="text-[#FFD60A] font-mono">{event.name}</code>
                  </td>
                  <td className="py-2 pr-4">
                    <span className="text-[#E85D24] font-bold">+{event.points}</span>
                  </td>
                  <td className="py-2 pr-4 text-[#A0A0A0]">{event.desc}</td>
                  <td className="py-2 text-[#555] font-mono text-[10px]">{event.method}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== SECTION 6: Custom Events ===== */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Custom Events</h2>
            <p className="text-sm text-[#A0A0A0]">For anything not auto-detected by widget.js</p>
          </div>
          <CopyBtn copied={copiedCustom} onClick={() => copyToClipboard(customEventsSnippet, setCopiedCustom, "Custom events")} label="Copy" />
        </div>
        <pre className="bg-[#050505] border border-[#2A2A2A] rounded p-4 overflow-x-auto text-xs font-mono text-[#A0A0A0] leading-relaxed">
          <code>{customEventsSnippet}</code>
        </pre>
      </div>

      {/* ===== SECTION 7: WATI Handoff Flow ===== */}
      <div className="bg-[#121212] border border-[#25D366]/40 rounded-sm p-6" data-testid="wati-handoff-section">
        <div className="flex items-center gap-3 mb-5">
          <WhatsappLogo size={24} weight="bold" className="text-[#25D366]" />
          <div>
            <h2 className="text-lg font-semibold text-white">WhatsApp Handoff via WATI</h2>
            <p className="text-sm text-[#A0A0A0]">Arya briefs your sales team, then steps aside</p>
          </div>
        </div>

        <div className="p-4 bg-[#0A0A0A] border border-[#25D366]/30 rounded mb-4">
          <p className="text-sm text-[#C0C0C0] mb-3">
            <span className="text-white font-medium">Arya does not respond on WhatsApp.</span>{" "}
            After qualifying a visitor on the website, Arya generates a structured handoff brief
            and sends it to your sales team in the WATI inbox — <em>before</em> the visitor's
            first WhatsApp message arrives.
          </p>
          <div className="flex flex-col gap-2">
            {[
              { num: "1", color: "#007AFF", text: "Visitor chats with Arya on portl.co. Arya captures lead info, scores intent." },
              { num: "2", color: "#BF5AF2", text: "Arya generates a handoff brief — goals, objections, product interest, next step." },
              { num: "3", color: "#25D366", text: "Brief is sent to WATI team inbox. Human agent picks up on WhatsApp with full context." },
              { num: "4", color: "#FF9F0A", text: "Hot lead alerts fire automatically when score crosses 65, cart URL shown, or demo booked." }
            ].map(step => (
              <div key={step.num} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center" style={{ background: step.color + "20", color: step.color }}>{step.num}</span>
                <p className="text-xs text-[#A0A0A0]">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="p-3 bg-[#050505] border border-[#2A2A2A] rounded">
          <p className="text-xs text-[#555] mb-1">WATI Inbound Webhook URL:</p>
          <code className="text-xs text-[#25D366] font-mono">{apiUrl}/api/wati-inbound</code>
        </div>
      </div>
    </div>
  );
};

export default IntegrationTab;
